import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  type ApiOutputs,
  templateLibraryCategoriesSchema,
  templateLibrarySchema,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import type { LibraryTemplate } from "@/common/types/templates";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";
import { getTemplateLanguage } from "@/utils/languageMapping";

const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    libraryCategories: {
      queryInterceptors: [
        async (options) => {
          if (typeof options.input === "symbol")
            throw Error("Missing library query");
          const items: (string | null)[] = [],
            seen = new Set<string | null>();
          let offset = 0;
          while (true) {
            options.fnContext.signal.throwIfAborted();
            const page = templateLibraryCategoriesSchema.parse(
              await options.next({
                ...options,
                input: {
                  query: {
                    ...options.input.query,
                    limit: "100",
                    offset: String(offset),
                  },
                },
              }),
            );
            if (
              page.items.length > 100 ||
              (page.nextOffset !== null &&
                (!page.items.length || page.nextOffset !== offset + 100))
            )
              throw Error("Invalid category page");
            for (const item of page.items) {
              if (seen.has(item)) throw Error("Duplicate category");
              seen.add(item);
              items.push(item);
            }
            if (page.nextOffset === null) return { items, nextOffset: null };
            offset = page.nextOffset;
          }
        },
      ],
    },
    libraryTemplates: {
      queryInterceptors: [
        async (options) => {
          if (typeof options.input === "symbol")
            throw Error("Missing library query");
          const items: ApiOutputs["libraryTemplates"]["items"] = [],
            seen = new Set<string>();
          let offset = 0;
          while (true) {
            options.fnContext.signal.throwIfAborted();
            const page = templateLibrarySchema.parse(
              await options.next({
                ...options,
                input: {
                  query: {
                    ...options.input.query,
                    limit: "100",
                    offset: String(offset),
                  },
                },
              }),
            );
            if (
              page.items.length > 100 ||
              (page.nextOffset !== null &&
                (!page.items.length || page.nextOffset !== offset + 100))
            )
              throw Error("Invalid library page");
            for (const item of page.items) {
              if (seen.has(item.public_id))
                throw Error("Duplicate library template");
              seen.add(item.public_id);
              items.push(item);
            }
            if (page.nextOffset === null) return { items, nextOffset: null };
            offset = page.nextOffset;
          }
        },
      ],
    },
  },
});
const key = (scope: VisitScope): readonly unknown[] => [
  "template-library",
  scope.uid,
  scope.accountPublicId,
  scope.generation,
];
const disabledScope: VisitScope = {
  uid: "",
  accountPublicId: "",
  generation: 0,
};
const categoryOptions = (scope: VisitScope, language: string) =>
  orpc.libraryCategories.queryOptions({
    queryKey: [...key(scope), "categories", language],
    input: { query: { language, limit: "100", offset: "0" } },
    context: { expectedUserUid: scope.uid },
    staleTime: 60_000,
    retry: false,
  });
export function useLibraryCategories(interfaceLanguage: string) {
  const scope = useVisitScope(),
    requestedLanguage = getTemplateLanguage(interfaceLanguage);
  const requested = useQuery({
    ...categoryOptions(scope ?? disabledScope, requestedLanguage),
    enabled: !!scope,
  });
  const fallback =
    requestedLanguage !== "en" &&
    requested.isSuccess &&
    requested.data.items.length === 0;
  const english = useQuery({
    ...categoryOptions(scope ?? disabledScope, "en"),
    enabled: !!scope && fallback,
  });
  const active = fallback ? english : requested;
  const refetch = active.refetch;
  const refresh = useCallback(async () => {
    if (scope && getVisitScope() === scope) await refetch();
  }, [scope, refetch]);
  return {
    categories: scope && active.isSuccess ? active.data.items : [],
    language: fallback ? "en" : requestedLanguage,
    requestedLanguage,
    loading: !!scope && active.isPending,
    error: active.isError,
    refresh,
  };
}
const toLibraryTemplate = (
  value: ApiOutputs["libraryTemplates"]["items"][number],
): LibraryTemplate => ({
  public_id: value.public_id,
  name: value.name,
  headers: value.headers,
  optionalText: value.optionalText ?? undefined,
  category: value.category,
  language: value.language,
  createDate: value.createdAt,
});
export function useLibraryTemplates(
  language: string,
  category: string | null,
  enabled: boolean,
) {
  const scope = useVisitScope();
  const query = useQuery(
    orpc.libraryTemplates.queryOptions({
      queryKey: [
        ...key(scope ?? disabledScope),
        "templates",
        language,
        category,
      ],
      input: {
        query: {
          language,
          limit: "100",
          offset: "0",
          ...(category === null ? { uncategorized: "true" } : { category }),
        },
      },
      context: { expectedUserUid: scope?.uid },
      enabled: !!scope && enabled,
      staleTime: 60_000,
      retry: false,
    }),
  );
  const refetch = query.refetch;
  const refresh = useCallback(async () => {
    if (enabled && scope && getVisitScope() === scope) await refetch();
  }, [enabled, scope, refetch]);
  return {
    templates:
      scope && enabled && query.isSuccess
        ? query.data.items.map(toLibraryTemplate)
        : [],
    loading: !!scope && enabled && query.isPending,
    error: enabled && query.isError,
    refresh,
  };
}
